import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse } from "../types/api";
import type { SessionDTO, CompanySessionDTO } from "./types";

/**
 * Tenant self-service device sessions (`/sessions`, relative to the
 * tenant API base):
 *  - `listMine`   → the caller's own active sessions
 *  - `revokeMine` → sign one of the caller's devices out
 *
 * The list endpoint returns a bare `SessionDTO[]` under `data` — the
 * list is small (capped per clientType), so it isn't paginated.
 *
 * Platform-admin session management lives on the platform repository
 * (`client.platform.listUserSessions` / `revokeSession`) because those
 * routes hang off the `/platform` prefix, not the tenant base.
 */
export function createSessionsRepository(fetcher: Fetcher) {
  return {
    listMine: async (): Promise<SessionDTO[]> => {
      const res = await fetcher<ApiSingleResponse<SessionDTO[]>>("/sessions");
      return res.data ?? [];
    },

    revokeMine: async (id: string): Promise<void> => {
      await fetcher(`/sessions/${id}`, { method: "DELETE" });
    },

    /**
     * Org-admin: every device across the caller's company, with the
     * owning user embedded. The Users settings tab joins these onto
     * the user rows by `user.id` to fill the Web / Mobile columns.
     * With no roles yet, every tenant user is an org admin.
     */
    listCompany: async (): Promise<CompanySessionDTO[]> => {
      const res = await fetcher<ApiSingleResponse<CompanySessionDTO[]>>(
        "/sessions/company",
      );
      return res.data ?? [];
    },

    /** Org-admin: revoke a session belonging to a fellow company user. */
    revokeForUser: async (id: string): Promise<void> => {
      await fetcher(`/sessions/company/${id}`, { method: "DELETE" });
    },
  };
}
